/**
 * PMT1 — Business-plane tenant context (ONE client-side authority).
 *
 * One verified person may hold memberships in several tenants. This module is the only place the
 * Web CRM reads that membership list, asks the SERVER to verify a requested tenant, writes the
 * selected tenant into the existing session store, and clears tenant-sensitive client state.
 *
 * INVARIANTS (mirror of the backend contract):
 *   * The selector is NOT an authorization decision. `verifySelectedTenant` only asks the server to
 *     confirm the caller holds an ACTIVE membership in the requested tenant; the server independently
 *     re-derives tenant + role on every subsequent request. A fabricated / foreign / revoked tenant
 *     is rejected here with the server's own 403 — the client never grants itself access.
 *   * The selected tenant is CLIENT/SESSION scoped. It lives in this browser's `crm_user_data` only;
 *     there is no server-global "currently selected tenant", so another device/session of the same
 *     person is unaffected.
 *   * No role union, no resource-classification union: the role written here is the one the server
 *     returned FOR THAT TENANT.
 */

const CRM_USER_DATA = 'crm_user_data';

/** Tenant-sensitive browser storage. Cleared whenever the tenant context changes. */
const TENANT_SCOPED_KEYS = ['currentBusiness', 'newConversationCustomer'];

const backend = () => process.env.REACT_APP_BACKEND_URL;

const readSession = () => {
  try { return JSON.parse(localStorage.getItem(CRM_USER_DATA) || '{}'); } catch { return {}; }
};

/**
 * The authenticated person's own tenant memberships, in the safe projection the backend authorises
 * ({ brand_id, business_name } only). Identity is server-derived from the bearer token — this call
 * never sends a phone/uid the caller could tamper with.
 */
export async function fetchMyMemberships(getToken) {
  const token = await getToken();
  if (!token) return { memberships: [], requires_tenant_selection: false };
  const res = await fetch(`${backend()}/api/v1/billing/staff/memberships`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return { memberships: [], requires_tenant_selection: false };
  const data = await res.json();
  return {
    memberships: Array.isArray(data.memberships) ? data.memberships : [],
    requires_tenant_selection: !!data.requires_tenant_selection,
  };
}

/**
 * Ask the SERVER to verify this requested tenant context. Resolves to the tenant-specific
 * { brand_id, business_name, role, staff_id } on success; throws on denial. The caller must not
 * enter a tenant without this resolving.
 */
export async function verifySelectedTenant(getToken, brandId) {
  const token = await getToken();
  if (!token) throw new Error('not_authenticated');
  const res = await fetch(
    `${backend()}/api/v1/billing/staff/memberships/${encodeURIComponent(brandId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(res.status === 403 ? 'membership_denied' : `verify_failed_${res.status}`);
  return res.json();
}

/**
 * Drop everything scoped to the PREVIOUS tenant. Called before entering a different tenant so no
 * Tenant-A row, roster, conversation, count, or cached business profile can survive into Tenant B.
 */
export function clearTenantScopedState() {
  for (const k of TENANT_SCOPED_KEYS) {
    try { localStorage.removeItem(k); } catch { /* storage unavailable */ }
    try { sessionStorage.removeItem(k); } catch { /* storage unavailable */ }
  }
}

/**
 * Persist a SERVER-VERIFIED tenant context into the existing session store. `verified` must be the
 * object returned by `verifySelectedTenant` — never a raw client-chosen brand id.
 */
export function persistSelectedTenant(verified) {
  const existing = readSession();
  const next = {
    ...existing,
    brandId: verified.brand_id,
    role: verified.role || existing.role,
    // PMT1 display identity: the canonical staff name FOR THE TENANT JUST ENTERED. Switching tenants
    // re-labels the person with that tenant's membership name; a raw phone is never preferred over a
    // real name. Falls back to whatever the session already held only when the tenant row has none.
    name: verified.name || existing.name || null,
    tenantSelectedAt: new Date().toISOString(),
  };
  localStorage.setItem(CRM_USER_DATA, JSON.stringify(next));
  return next;
}

/**
 * Full switch: verify with the server, purge the old tenant, persist the new context, then hard-load
 * the Business shell.
 *
 * The hard load is deliberate. Rebuilding the document is the only way to guarantee that EVERY
 * tenant-sensitive surface — React contexts, in-memory query caches, the dashboard, staff roster,
 * customers, conversations, appointments, jobs, notifications, role/permissions, the cached business
 * profile, and the realtime/WebSocket tenant binding (the socket path carries business_id) — is
 * rebuilt for the new tenant. Selectively invalidating caches would leave residue.
 */
export async function switchTenant(getToken, brandId) {
  const verified = await verifySelectedTenant(getToken, brandId);
  clearTenantScopedState();
  persistSelectedTenant(verified);
  window.location.assign('/business');
  return verified;
}

/**
 * The tenant this browser session is currently acting in, read from the SAME persisted selection
 * switchTenant/persistSelectedTenant own. Every protected Business call stamps it as a header.
 *
 * PMT1: one verified person may hold memberships in several tenants, so identity alone cannot tell
 * the server which business is on screen. Routes carrying the tenant in their path were always
 * scoped correctly; the /me/* routes carried nothing, so the server resolved the caller from an
 * arbitrary membership and served another tenant's commercial receipts, entitlements and plan
 * version under the selected business.
 *
 * This is a SELECTION, never an authorisation. The server re-verifies that the caller holds an
 * active membership in this tenant and derives role/tenant from THAT membership; a forged or stale
 * value resolves to no membership and fails closed rather than widening access.
 */
export function getSelectedTenant() {
  try { return (readSession().brandId || '').trim(); } catch { return ''; }
}

/**
 * Publish the tenant the Business shell has RESOLVED as active into the same persisted field
 * `persistSelectedTenant` owns. There is exactly one stored active tenant; this is a second WRITER
 * of that one field, not a second store.
 *
 * Needed because `switchTenant` is not the only way a tenant becomes active: on first login, and
 * after a signup, the shell resolves the tenant from the login payload or the staff record, and
 * nothing wrote it back. The header was then empty and every /me/* call failed closed with 409 —
 * correct refusal, wrong cause. Writing the resolved tenant here keeps the header equal to the
 * business the UI is actually displaying.
 *
 * Blank values are ignored rather than persisted: clearing is owned by `clearTenantScopedState` and
 * sign-out, so a transient null while the shell re-resolves cannot erase a valid selection.
 */
export function setActiveTenant(brandId) {
  const next = (brandId || '').trim();
  if (!next) return;
  try {
    const existing = readSession();
    if (existing.brandId === next) return;
    localStorage.setItem(CRM_USER_DATA, JSON.stringify({ ...existing, brandId: next }));
  } catch { /* storage unavailable - the header is simply omitted and the server fails closed */ }
}

/** Request header carrying the selected tenant. Mirrors repository.ACTIVE_TENANT_HEADER. */
export const ACTIVE_TENANT_HEADER = 'X-Servisfy-Tenant';

/**
 * The active-tenant header for an authenticated Business-plane request, or `{}` when no tenant is
 * selected. THE one place the header is built — transports spread it next to Authorization.
 *
 * Why this exists: the Business shell already knew the active tenant (the UI renders its name), but
 * each authenticated transport built headers by hand and only ever attached the bearer token. Routes
 * that carry their tenant in the PATH were unaffected, so the gap was invisible until the backend
 * stopped guessing: every tenant-sensitive route WITHOUT a path tenant (/mfa/status, /mfa/policy,
 * /status/updates, /me/service-workflow-eligibility) then failed closed with 409 for anyone holding
 * more than one membership. The tenant was never missing — it was never sent.
 *
 * This is not a second tenant store: it reads the one selection `persistSelectedTenant` /
 * `setActiveTenant` own. It is a SELECTION, never an authorisation — the server re-derives tenant and
 * role from the caller's membership, so a forged or stale value fails closed rather than widening.
 *
 * Business plane only. Customer-plane clients must NOT send it.
 */
export function tenantHeaders() {
  const brandId = getSelectedTenant();
  return brandId ? { [ACTIVE_TENANT_HEADER]: brandId } : {};
}

export const __TENANT_SCOPED_KEYS = TENANT_SCOPED_KEYS;
